import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  BehaviorSubject,
  Subject,
  combineLatest,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs';
import {
  DataBoundEventArgs,
  DrawNodeEventArgs,
  FieldSettingsModel,
  NodeCheckEventArgs,
  NodeClickEventArgs,
  NodeSelectEventArgs,
  TreeViewComponent as SyncfusionTreeViewComponent,
} from '@syncfusion/ej2-angular-navigations';
import * as _ from 'lodash';

@Component({
  selector: 'app-tree-view',
  templateUrl: './tree-view.component.html',
  styleUrls: ['./tree-view.component.scss'],
})
export class TreeViewComponent implements OnInit, AfterViewInit {
  @Input() allowSelection = false;

  /** Passed through to the search box. Already translated by the caller. */
  @Input() searchPlaceholder = '';

  private _baseData: OrgTreeView[];
  @Input() set baseData(value: OrgTreeView[]) {
    this._baseData = value;
    // A filter in the box outlives a data change - re-derive the results rather than dropping the
    // user back to the full tree with their search term still on screen.
    this.data$.next(this.searchText ? this.search(this.searchText) : value);
  }
  get baseData(): OrgTreeView[] {
    return this._baseData;
  }

  private _selected: number[];
  @Input() set selected(value: number[]) {
    if (this.initialSelection === undefined) {
      this.initialSelection = value;
    }
    if (!this.haveSameValues(value, this._selected)) {
      this.selectedChange.emit(value);
    }
    this._selected = value;
  }
  get selected(): number[] {
    return this._selected;
  }

  @Input() max: number | null = null;
  @Input() min: number | null = null;
  @Input() field: OrgTreeSettingsModel = {
    dataSource: [],
    id: 'id',
    text: 'name',
    parentID: 'parentId',
  };

  @Output() selectedChange = new EventEmitter<number[]>();
  data$: BehaviorSubject<OrgTreeView[]> = new BehaviorSubject<OrgTreeView[]>(
    [],
  );
  path$: Subject<OrgTreeView[]> = new Subject<OrgTreeView[]>();
  viewInitialized$: Subject<void> = new Subject<void>();

  destroy$: Subject<void> = new Subject<void>();

  @ViewChild('tree') tree: SyncfusionTreeViewComponent;

  initialSelection: number[];
  disabled: { id: number; selected: boolean }[];
  path: OrgTreeView[];
  searchText: string;

  constructor() {
    combineLatest([this.selectedChange, this.data$, this.viewInitialized$])
      .pipe(
        distinctUntilChanged(),
        map(([selected, data]) => {
          // Process selected
          data = this.initializeData(data);
          this.ensureDisabledSelection();
          // Every assignment to `expandedNodes` collapses the whole tree first - Syncfusion's
          // onPropertyChanged for it calls collapseAll() and then re-expands only the ids listed
          // here - and this runs on every emission, including the ones that merely swap the data
          // source. That makes this list, not the data, the last word on what is open: a node
          // carrying `expanded: true` renders open and is then shut again by the next assignment.
          //
          // So the data's own `expanded` flag is folded IN rather than relied on. Callers that set
          // it (the affiliate picker's narrow view, the participation child picker, and search
          // below) get what they asked for, and callers that do not are unaffected - the filter is
          // empty for them.
          const expanded = [
            ...new Set([
              ...(this.tree.expandedNodes || []),
              ...data.filter((node) => node.expanded).map((node) => node.id.toString()),
              ...this.getNodeAndParents(selected).map((x) => x.id.toString()),
            ]),
          ];
          this.tree.expandedNodes = expanded;

          // Process data
          return data;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((data) => {
        this.field = {
          ...this.field,
          dataSource: data,
        };
      });
  }

  ngAfterViewInit() {
    this.viewInitialized$.next();
  }

  ngOnInit(): void {
    this.allowSelection = this.max !== 0 || this.min !== 0;
  }

  onNodeSelect(args: NodeClickEventArgs) {}

  initializeData(data: OrgTreeView[]): OrgTreeView[] {
    this.disabled = data
      .filter((x) => x.disabled === true)
      .map((x) => {
        return { id: x.id, selected: x.selected || false };
      });
    data
      .filter((x) => x.normalizedName === undefined)
      .forEach((x) => {
        x.normalizedName = this.normalizeText(x.name);
      });
    return data;
  }

  onDataBound(evet: DataBoundEventArgs) {
    this.tree.checkAll(this.selected.map((id) => id.toString()));
  }

  onNodeSelected(event: NodeSelectEventArgs) {
    event.cancel = true;
  }

  onNavigate(node: OrgTreeView) {
    this.data$.next([node]);
  }

  onNodeChecking(node: NodeCheckEventArgs) {
    const singleSelect = this.max === 1 && this.min === 1;
    const count = this.tree.checkedNodes.length;

    // Disabled was cosmetic until now: 'disabled' is not part of the fields mapping, so Syncfusion
    // has no notion of it and happily checks a greyed node. Only interactive checks are refused -
    // ensureDisabledSelection drives disabled nodes programmatically and must still get through.
    if (node.isInteracted && this.isDisabledNode(node)) {
      node.cancel = true;
      return;
    }

    if (singleSelect) {
      // The default selection is a single selection and we have a disabled selected node, we can't do anything!
      if (this.disabled.filter((x) => x.selected).length > 0 && count >= 1) {
        node.cancel = true;
        return;
      }
    }

    if (node.action === 'check') {
      if (singleSelect && count > 0) {
        this.tree.uncheckAll(this.tree.checkedNodes);
        return;
      }
      if (this.max !== null && count >= this.max) {
        node.cancel = true;
        return;
      }
    }

    if (node.action === 'uncheck') {
      const reverseCount = this.baseData.length - count;

      if (singleSelect) {
        return;
      }

      if (this.min !== null && reverseCount <= this.min) {
        node.cancel = true;
        return;
      }
    }
  }

  private isDisabledNode(node: NodeCheckEventArgs): boolean {
    const id = node.node?.getAttribute('data-uid');
    return id !== null && this.disabled.some((x) => x.id.toString() === id);
  }

  onChecked(node: NodeCheckEventArgs) {
    if (node.data === undefined || node.data.length === 0) {
      node.cancel = true;
      return;
    }
    const ids = this.tree.checkedNodes.map((x) => parseInt(x));
    this.selected = ids;
  }

  haveSameValues<T>(arr1: T[] | null | undefined, arr2: T[] | null | undefined): boolean {
    // Handle null or undefined arrays
    if (arr1 == null && arr2 == null) {
      return true;
    }
    if (arr1 == null || arr2 == null) {
      return false;
    }

    // Handle arrays with different lengths
    if (arr1.length !== arr2.length) {
      return false;
    }

    // Sort and compare arrays
    return _.isEqual(_.sortBy(arr1), _.sortBy(arr2));
  }

  private getNodeAndParents(selectedIds: number[]): OrgTreeView[] {
    const results: OrgTreeView[] = [];
    const visited = new Set<number>();

    const addNodeAndParents = (nodeId: number) => {
      let currentNode = this.baseData.find((node) => node.id === nodeId);
      while (currentNode && !visited.has(currentNode.id)) {
        results.push(currentNode);
        visited.add(currentNode.id);
        currentNode =
          currentNode.parentId != null
            ? this.baseData.find((x) => x.id === currentNode.parentId)
            : null;
      }
    };

    selectedIds.forEach((id) => addNodeAndParents(id));

    return results;
  }

  private ensureDisabledSelection(): number[] {
    const forceSelected = this.disabled
      .filter((x) => x.selected)
      .map((x) => x.id.toString());
    const forceUnselected = this.disabled
      .filter((x) => !x.selected)
      .map((x) => x.id.toString());
    const checked = this._selected.map((x) => x.toString());
    const unchecked = this.baseData
      .map((x) => x.id.toString())
      .filter((x) => !checked.includes(x));

    const toUnselect = checked.filter((x) => forceUnselected.includes(x));
    if (toUnselect.length > 0) {
      this.tree.uncheckAll(toUnselect);
      return this.selected.filter((x) => !toUnselect.includes(x.toString()));
    }

    const toSelect = unchecked.filter((x) => forceSelected.includes(x));
    if (toSelect.length > 0) {
      const selected = [
        ...new Set([...this.selected, ...toSelect.map((x) => parseInt(x))]),
      ];
      this.tree.checkAll(toSelect);
      return selected;
    }
  }

  onIdNavigate(id: number) {}

  onSearch(search: string) {
    const searchResults = this.search(search);
    let data = searchResults;
    this.data$.next(data);
    this.searchText = search;
  }

  onReset() {
    this.data$.next(this.baseData);
    this.searchText = '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  search(searchString: string): OrgTreeView[] {
    if (searchString === '') {
      return this.baseData;
    }

    const normalizedSearchString = this.normalizeText(searchString);
    const results: OrgTreeView[] = [];
    const visited = new Set<number>();

    const addNodeAndParents = (node: OrgTreeView) => {
      let currentNode: OrgTreeView | null = node;
      while (currentNode && !visited.has(currentNode.id)) {
        results.push({
          ...currentNode,
          expanded: true,
        });
        visited.add(currentNode.id);
        const parentIndex = this.baseData.findIndex(
          (x) => x.id === currentNode?.parentId,
        );
        currentNode = parentIndex !== -1 ? this.baseData[parentIndex] : null;
      }
    };

    this.baseData.forEach((node) => {
      if (node.normalizedName.includes(normalizedSearchString)) {
        this.markAsMatch(node, searchString);
        addNodeAndParents(node);
      }
    });

    const order = new Map<number, number>(
      this.baseData.map((node, index) => [node.id, index]),
    );

    return results.sort((a, b) => (order?.get(a.id) ?? 0) - (order?.get(b.id) ?? 0));
  }

  private markAsMatch(node: OrgTreeView, query: string) {
    const matchStart = node.name.toLowerCase().indexOf(query.toLowerCase());
    node.matchStart = matchStart;
    node.matchEnd = matchStart + query.length;
    node.isMatch = true;
  }

  onDrawNode(args: DrawNodeEventArgs): void {
    const ele: HTMLElement = args.node.querySelector('.e-checkbox-wrapper');
    if (ele === null) {
      return;
    }
    // Both directions. Nodes are redrawn in place when the data changes, so only adding the class
    // left a node greyed for the life of the dialog once anything had disabled it.
    const disabled = args.nodeData.disabled === true;
    ele.classList.toggle('e-checkbox-disabled', disabled);
    // The checkbox alone is a weak signal on a node whose label still reads as normal text. The
    // label is greyed from _syncfusion-overrides.scss, which owns everything inside .e-list-item.
    args.node.classList.toggle('tl-node-unselectable', disabled);
  }

  private normalizeText(input: string): string {
    // Normalize by removing non-alphanumeric characters and extra spaces, converting to lowercase
    return input
      .replace(/[\W_]+/g, ' ')
      .trim()
      .toLowerCase();
  }
}

export class OrgTreeView {
  id: number;
  name: string;
  normalizedName: string;
  parentId: number | null;
  hasChildren: boolean;

  expanded?: boolean = true;

  disabled?: boolean = false;

  /**
   * Whether this node may be picked. Set by the affiliate picker, where a node can be in the tree
   * purely as a parent for something below it; other consumers leave it undefined and are
   * unaffected. The picker folds it into `disabled`, which is what the tree acts on.
   */
  selectable?: boolean;

  isMatch?: boolean = false;
  matchStart?: number;
  matchEnd?: number;

  selected?: boolean = false;
}

type SearchResult = Map<number, { start: number; end: number }>;

export class OrgTreeSettingsModel implements FieldSettingsModel {
  dataSource: OrgTreeView[];
  id: string;
  text: string;
  parentID: string;
}
